import React, { useRef, useState } from 'react'

import { BlogContent } from '@application/Interfaces/Features/BlogContent'
import { mapToBlogCard as mapBlogToBlogCard } from '@application/Interfaces/Features/BlogList/utils'
import { BlogsThumbnailCards } from '@application/Interfaces/Features/BlogsThumbnailCards/BlogsThumbnailCards'
import { Breadcrumb } from '@application/Interfaces/Features/Breadcrumb'
import { mapToBlogCard as mapHelpCenterToBlogCard } from '@application/Interfaces/Features/HelpCenterList/HelpCenterListView/utils'
import { useScrollPosition } from '@application/Interfaces/Hooks/useScrollPosition/useScrollPosition'
import { useTranslation } from '@src/Application/Interfaces/Hooks/useTranslation'
import {
    BlogButtonType,
    BlogContentType,
    BlogDetailsType,
    HelpCenterDetailsType,
} from '@src/Application/Types/ComponentTypes'
import { Nullable } from '@src/Application/Types/Opt'
import { STRAPI_COMPONENT_TYPES } from '@src/Core/Constants/PageConstants'

import { Container, ContentWrapper, ProgressScroll, SideContainer, SideWrapper, Wrapper } from './BlogDetails.styled'
import { ContentGuide } from './ContentGuide'
import { findContentGuideHeadings, getRelatedArticles, onProgressScroll, onScroll } from './utils'

export const BlogDetails = (props: BlogDetailsType | HelpCenterDetailsType): JSX.Element => {
    const { id, components, category, title, showContentGuideSubmenu } = props
    const list: string[] = []
    const wrapperRef = useRef(null)
    const sidebarRef = useRef(null)
    const contentWrapperRef = useRef(null)
    const [sidebarState, setSidebarState] = useState<Nullable<string>>(null)
    const [progressState, setProgressState] = useState<number>(0)
    const [progressPositionState, setProgressPositionState] = useState<number>(0)
    const { t } = useTranslation()

    const html = components.reduce((prev, curr) => {
        if (curr.strapi_component === STRAPI_COMPONENT_TYPES.BLOG_CONTENT) {
            prev.push((curr as unknown as BlogContentType).content.data.childMarkdownRemark.html)
        }

        return prev
    }, list)

    useScrollPosition(onScroll({ setSidebarState, wrapperRef, sidebarRef }))
    useScrollPosition(onProgressScroll({ setProgressState, setProgressPositionState, wrapperRef: contentWrapperRef }))

    const contentGuideList = findContentGuideHeadings(html.join(''))

    let relatedArticles

    if ('blogs' in props.category) {
        relatedArticles = mapBlogToBlogCard(getRelatedArticles(props.category.blogs, title.data.title))
    }

    if ('help_centers' in props.category) {
        relatedArticles = mapHelpCenterToBlogCard(getRelatedArticles(props.category.help_centers, title.data.title))
    }

    const breadcrumbs = [
        { title: t('home'), link: '/' },
        { title: category.collectionName, link: category.collectionUrl },
        { title: category.title, link: category.categoryUrl },
        { title: title.data.title },
    ]

    return (
        <>
            <ProgressScroll $progress={progressState} $progressPosition={progressPositionState} />
            <Container>
                <Breadcrumb id="breadcrumb" strapi_component="breadcrumb" items={breadcrumbs} />
                <Wrapper ref={contentWrapperRef}>
                    <ContentWrapper>
                        <BlogContent
                            components={components as (BlogContentType | BlogButtonType)[]}
                            id={id}
                            strapi_component={STRAPI_COMPONENT_TYPES.BLOG_CONTENT}
                        />
                    </ContentWrapper>

                    <SideWrapper ref={wrapperRef}>
                        <SideContainer $state={sidebarState} ref={sidebarRef}>
                            <ContentGuide items={contentGuideList} showContentGuideSubmenu={showContentGuideSubmenu} />
                        </SideContainer>
                    </SideWrapper>
                </Wrapper>
                {relatedArticles && relatedArticles.length > 0 && (
                    <BlogsThumbnailCards
                        id="related-articles"
                        strapi_component={STRAPI_COMPONENT_TYPES.BLOG}
                        blogTitle={t('relatedArticles')}
                        viewAllLink={props.category.categoryUrl}
                        viewAllLinkLabel={t('viewAll')}
                        mode="Light"
                        subtitle=""
                        blogDescription=""
                        BlogItem={relatedArticles}
                    />
                )}
            </Container>
        </>
    )
}
